import { useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { GripVertical, Plus, Search, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { api, type InspirationFeedItem, type InspirationIdeaFeedItem, type Product } from "@/api";
import { inspirationFeedKeys, inspirationIdeaKeys, useInspirationFeed, useInspirationIdeas } from "@/api/hooks/use-inspiration";
import { productKeys, useProducts } from "@/api/hooks/use-products";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@osmix/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn, formatPrice } from "@/lib/utils";
const moveItem = <T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
    return items;
  }
  const next = [...items];
  const [picked] = next.splice(fromIndex, 1);
  if (picked === undefined) return items;
  next.splice(toIndex, 0, picked);
  return next;
};
const matchesProductSearch = (product: Product, rawQuery: string) => {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return true;
  return [product.name, product.article, product.brand, product.category, product.color, product.description, ...product.keywords].join(" ").toLowerCase().includes(query);
};
type FeedTab = "products" | "ideas";
export function InspirationFeedPage() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<FeedTab>("products");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [draggingProductId, setDraggingProductId] = useState<string | null>(null);
  const [productDropTargetId, setProductDropTargetId] = useState<string | null>(null);
  const [draggingIdeaId, setDraggingIdeaId] = useState<string | null>(null);
  const [ideaDropTargetId, setIdeaDropTargetId] = useState<string | null>(null);
  const feedQuery = useInspirationFeed();
  const ideasQuery = useInspirationIdeas();
  // While the picker is open its server-side search drives the product list,
  // mirroring how the page previously swapped the fetched set in place.
  const productsQuery = useProducts(pickerOpen ? {
    status: "published",
    page: 1,
    pageSize: 100,
    search: productSearch
  } : {
    status: "published",
    page: 1,
    pageSize: 200
  });
  const productItems = feedQuery.data ?? [];
  const ideaItems = ideasQuery.data ?? [];
  const publishedProducts = productsQuery.data?.data ?? [];
  // The picker's search fetches must not flip the page into its initial
  // loading state — only the first load did that before.
  const loading = feedQuery.isPending || ideasQuery.isPending || !pickerOpen && productsQuery.isPending;
  const loadError = feedQuery.error ?? ideasQuery.error ?? productsQuery.error;
  const error = actionError ?? (loadError ? loadError.message : null);
  const refresh = async () => {
    setActionError(null);
    await Promise.all([queryClient.invalidateQueries({
      queryKey: inspirationFeedKeys.all
    }), queryClient.invalidateQueries({
      queryKey: inspirationIdeaKeys.all
    }), queryClient.invalidateQueries({
      queryKey: productKeys.all
    })]);
  };
  const availableProducts = publishedProducts.filter(product => !productItems.some(item => item.productId === product.id));
  const filteredProducts = availableProducts.filter(product_0 => matchesProductSearch(product_0, productSearch));
  const persistProductOrder = async (nextItems: InspirationFeedItem[]) => {
    setBusy(true);
    setActionError(null);
    try {
      queryClient.setQueryData(inspirationFeedKeys.all, await api.inspirationFeed.reorder(nextItems.map(item_0 => item_0.id)));
    } catch (e) {
      setActionError((e as Error).message);
      await refresh();
    } finally {
      setBusy(false);
    }
  };
  const persistIdeaOrder = async (nextItems_0: InspirationIdeaFeedItem[]) => {
    setBusy(true);
    setActionError(null);
    try {
      queryClient.setQueryData(inspirationIdeaKeys.all, await api.inspirationIdeas.reorder(nextItems_0.map(item_1 => item_1.id)));
    } catch (e_0) {
      setActionError((e_0 as Error).message);
      await refresh();
    } finally {
      setBusy(false);
    }
  };
  const pickProduct = async (product_1: Product) => {
    setBusy(true);
    setActionError(null);
    try {
      await api.inspirationFeed.add({
        productId: product_1.id
      });
      setPickerOpen(false);
      setProductSearch("");
      await refresh();
    } catch (e_1) {
      setActionError((e_1 as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const removeProductItem = async (item_2: InspirationFeedItem) => {
    if (!confirm(t("inspirationFeed.confirmRemove", {
      name: item_2.product.name
    }))) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.inspirationFeed.remove(item_2.id);
      await refresh();
    } catch (e_2) {
      setActionError((e_2 as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const onProductDragStart = (event: DragEvent<HTMLElement>, itemId: string) => {
    setDraggingProductId(itemId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", itemId);
  };
  const onProductDrop = async (event_0: DragEvent<HTMLElement>, targetId: string) => {
    event_0.preventDefault();
    const sourceId = draggingProductId ?? event_0.dataTransfer.getData("text/plain");
    setDraggingProductId(null);
    setProductDropTargetId(null);
    if (!sourceId || sourceId === targetId) return;
    const fromIndex = productItems.findIndex(item_3 => item_3.id === sourceId);
    const toIndex = productItems.findIndex(item_4 => item_4.id === targetId);
    const next = moveItem(productItems, fromIndex, toIndex);
    if (next === productItems) return;
    queryClient.setQueryData(inspirationFeedKeys.all, next);
    await persistProductOrder(next);
  };
  const onIdeaDragStart = (event_1: DragEvent<HTMLElement>, itemId_0: string) => {
    setDraggingIdeaId(itemId_0);
    event_1.dataTransfer.effectAllowed = "move";
    event_1.dataTransfer.setData("text/plain", itemId_0);
  };
  const onIdeaDrop = async (event_2: DragEvent<HTMLElement>, targetId_0: string) => {
    event_2.preventDefault();
    const sourceId_0 = draggingIdeaId ?? event_2.dataTransfer.getData("text/plain");
    setDraggingIdeaId(null);
    setIdeaDropTargetId(null);
    if (!sourceId_0 || sourceId_0 === targetId_0) return;
    const fromIndex_0 = ideaItems.findIndex(item_5 => item_5.id === sourceId_0);
    const toIndex_0 = ideaItems.findIndex(item_6 => item_6.id === targetId_0);
    const next_0 = moveItem(ideaItems, fromIndex_0, toIndex_0);
    if (next_0 === ideaItems) return;
    queryClient.setQueryData(inspirationIdeaKeys.all, next_0);
    await persistIdeaOrder(next_0);
  };
  return <div className="space-y-6">
			<div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
				<div className="space-y-1">
					<h1 className="text-xl font-semibold">{t("inspirationFeed.heading")}</h1>
					<p className="max-w-2xl text-sm text-muted-foreground">
						{t("inspirationFeed.description")}
					</p>
				</div>
				<Badge variant="secondary">
					{activeTab === "products" ? t("inspirationFeed.itemCount", {
          count: productItems.length
        }) : t("inspirationFeed.ideaCount", {
          count: ideaItems.length
        })}
				</Badge>
			</div>

			<div className="inline-flex rounded-lg border bg-muted p-1">
				<Button variant={activeTab === "products" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("products")}>
					{t("inspirationFeed.tabs.products")}
				</Button>
				<Button variant={activeTab === "ideas" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("ideas")}>
					{t("inspirationFeed.tabs.ideas")}
				</Button>
			</div>

			{activeTab === "products" ? <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 md:flex-row md:items-center md:justify-between">
					<div className="space-y-1">
						<p className="text-sm font-medium">{t("inspirationFeed.addProduct")}</p>
						<p className="text-sm text-muted-foreground">
							{t("inspirationFeed.availableProducts", {
            count: availableProducts.length
          })}
						</p>
					</div>
					<Button onClick={() => setPickerOpen(true)} disabled={busy || loading}>
						<Plus className="size-4" /> {t("inspirationFeed.openPicker")}
					</Button>
				</div> : <div className="rounded-lg border bg-card p-4">
					<p className="text-sm font-medium">{t("inspirationFeed.ideasPanelTitle")}</p>
					<p className="text-sm text-muted-foreground">
						{t("inspirationFeed.ideasPanelDescription")}
					</p>
				</div>}

			{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{error}
				</div>}

			<Dialog open={pickerOpen} onOpenChange={open => {
      setPickerOpen(open);
      if (!open) {
        setProductSearch("");
        void refresh();
      }
    }}>
				<DialogContent className="max-h-[90vh] overflow-hidden sm:max-w-5xl">
					<DialogHeader>
						<DialogTitle>{t("inspirationFeed.pickerTitle")}</DialogTitle>
						<DialogDescription>{t("inspirationFeed.pickerDescription")}</DialogDescription>
					</DialogHeader>

					<div className="relative">
						<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<Input autoFocus value={productSearch} onChange={event_3 => setProductSearch(event_3.target.value)} placeholder={t("inspirationFeed.searchPlaceholder")} className="pl-9" />
					</div>

					<div className="max-h-[60vh] overflow-auto rounded-md border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-[72px]">{t("products.col.photo")}</TableHead>
									<TableHead>{t("inspirationFeed.product")}</TableHead>
									<TableHead>{t("products.col.category")}</TableHead>
									<TableHead>{t("inspirationFeed.article")}</TableHead>
									<TableHead className="text-right">{t("products.col.price")}</TableHead>
									<TableHead className="w-[130px] text-right">
										{t("products.col.actions")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filteredProducts.length === 0 && <TableRow>
										<TableCell colSpan={6} className="text-center text-muted-foreground">
											{t("inspirationFeed.noProducts")}
										</TableCell>
									</TableRow>}
								{filteredProducts.map(product_2 => <TableRow key={product_2.id}>
										<TableCell>
											{product_2.photos[0] ? <img src={product_2.photos[0]} alt="" className="size-12 rounded object-cover" /> : <div className="size-12 rounded bg-muted" />}
										</TableCell>
										<TableCell>
											<div className="font-medium">{product_2.name}</div>
											<div className="text-sm text-muted-foreground">{product_2.brand}</div>
										</TableCell>
										<TableCell className="text-muted-foreground">{product_2.category}</TableCell>
										<TableCell className="text-muted-foreground">{product_2.article}</TableCell>
										<TableCell className="text-right tabular-nums">
											{formatPrice(product_2.price)}
										</TableCell>
										<TableCell className="text-right">
											<Button size="sm" onClick={() => void pickProduct(product_2)} disabled={busy}>
												{t("inspirationFeed.pick")}
											</Button>
										</TableCell>
									</TableRow>)}
							</TableBody>
						</Table>
					</div>
				</DialogContent>
			</Dialog>

			{loading ? <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
					{t("inspirationFeed.loading")}
				</div> : activeTab === "products" ? productItems.length === 0 ? <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
						{t("inspirationFeed.empty")}
					</div> : <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
						{productItems.map((item_7, index) => <Card key={item_7.id} draggable={!busy} onDragStart={event_4 => onProductDragStart(event_4, item_7.id)} onDragEnd={() => {
        setDraggingProductId(null);
        setProductDropTargetId(null);
      }} onDragOver={event_5 => event_5.preventDefault()} onDragEnter={() => setProductDropTargetId(item_7.id)} onDrop={event_6 => void onProductDrop(event_6, item_7.id)} className={cn("overflow-hidden py-0 transition", draggingProductId === item_7.id && "opacity-50", productDropTargetId === item_7.id && draggingProductId !== item_7.id && "ring-2 ring-primary")}>
								<div className="relative aspect-[4/3] bg-muted">
									{item_7.product.photos[0] ? <img src={item_7.product.photos[0]} alt="" className="size-full object-cover" /> : <div className="flex size-full items-center justify-center text-sm text-muted-foreground">
											{t("productDetail.noPhotos")}
										</div>}
									<Badge className="absolute left-3 top-3">#{index + 1}</Badge>
								</div>
								<CardHeader className="grid-cols-[1fr_auto] gap-3 p-4">
									<div className="space-y-1">
										<CardTitle>{item_7.editorialTitle || item_7.product.name}</CardTitle>
										<CardDescription>
											{item_7.product.brand} · {item_7.product.category}
										</CardDescription>
									</div>
									<GripVertical className="mt-1 size-5 cursor-grab text-muted-foreground" />
								</CardHeader>
								<CardContent className="space-y-4 p-4 pt-0">
									{item_7.editorialNote && <p className="text-sm text-muted-foreground">{item_7.editorialNote}</p>}
									<div className="flex items-center justify-between gap-3">
										<span className="text-sm font-medium tabular-nums">
											{formatPrice(item_7.product.price)}
										</span>
										<Button size="sm" variant="outline" onClick={() => void removeProductItem(item_7)} disabled={busy}>
											<Trash2 className="size-4" /> {t("inspirationFeed.remove")}
										</Button>
									</div>
								</CardContent>
							</Card>)}
					</div> : ideaItems.length === 0 ? <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
					{t("inspirationFeed.emptyIdeas")}
				</div> : <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
					{ideaItems.map((item_8, index_0) => <Card key={item_8.id} draggable={!busy} onDragStart={event_7 => onIdeaDragStart(event_7, item_8.id)} onDragEnd={() => {
        setDraggingIdeaId(null);
        setIdeaDropTargetId(null);
      }} onDragOver={event_8 => event_8.preventDefault()} onDragEnter={() => setIdeaDropTargetId(item_8.id)} onDrop={event_9 => void onIdeaDrop(event_9, item_8.id)} className={cn("overflow-hidden py-0 transition", draggingIdeaId === item_8.id && "opacity-50", ideaDropTargetId === item_8.id && draggingIdeaId !== item_8.id && "ring-2 ring-primary")}>
							<div className="relative aspect-[4/3] bg-muted">
								<img src={item_8.idea.coverPhoto} alt="" className="size-full object-cover" />
								<Badge className="absolute left-3 top-3">#{index_0 + 1}</Badge>
							</div>
							<CardHeader className="grid-cols-[1fr_auto] gap-3 p-4">
								<div className="space-y-1">
									<CardTitle>{item_8.idea.title}</CardTitle>
									<CardDescription>
										{t("inspirationFeed.ideaProducts", {
                count: item_8.idea.productCount
              })}
									</CardDescription>
								</div>
								<GripVertical className="mt-1 size-5 cursor-grab text-muted-foreground" />
							</CardHeader>
							<CardContent className="p-4 pt-0">
								<p className="text-sm text-muted-foreground">{item_8.idea.description}</p>
							</CardContent>
						</Card>)}
				</div>}
		</div>;
}